import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  User, Camera, Lock, Calendar, Clock, Play, Ticket, 
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, Edit2, ChevronRight, 
  Home, Mail, Instagram, LogOut, Film, ShoppingCart, Crown, Tv
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveCustomerAccount = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [restrictedEvent, setRestrictedEvent] = useState(null);
  const [isVodUser, setIsVodUser] = useState(false); // Track if user came from VOD
  
  // Profile states
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [instagramUsername, setInstagramUsername] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  
  // Password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // VOD states
  const [vodData, setVodData] = useState({ available_for_purchase: [], already_owned: [] });
  const [myVods, setMyVods] = useState([]);
  const [loadingVods, setLoadingVods] = useState(false);
  const [purchasingVod, setPurchasingVod] = useState(null);
  
  // VOD Membership states
  const [vodMembership, setVodMembership] = useState(null);
  const [vodPurchases, setVodPurchases] = useState([]);
  const [loadingVodMembership, setLoadingVodMembership] = useState(false);
  
  const [message, setMessage] = useState({ type: '', text: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('livestreamToken')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      navigate('/live/login');
      return;
    }
    
    // Check if user came from VOD (either by URL or by checking referrer/source)
    const urlParams = new URLSearchParams(window.location.search);
    const fromVod = urlParams.get('from') === 'vod' || 
                    window.location.hostname.includes('ltvvod') ||
                    document.referrer.includes('/vod') ||
                    localStorage.getItem('userSource') === 'vod';
    
    if (fromVod) {
      setIsVodUser(true);
      localStorage.setItem('userSource', 'vod'); // Remember for future visits
    }
    
    // Check if coming from a restricted event (only applies to Live events, not VOD)
    const restrictedEventData = localStorage.getItem('restrictedEvent');
    if (restrictedEventData && !fromVod) {
      try {
        setRestrictedEvent(JSON.parse(restrictedEventData));
      } catch (e) {
        console.error('Error parsing restricted event data:', e);
      }
    }
    
    fetchDashboard();
    fetchVodData();
    fetchVodMembershipData();
  }, [navigate]);

  const fetchVodMembershipData = async () => {
    setLoadingVodMembership(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/vod/my-library`, getAuthHeaders());
      setVodMembership(res.data.subscription);
      setVodPurchases(res.data.purchased_videos || []);
    } catch (err) {
      console.error('Error fetching VOD membership:', err);
    } finally {
      setLoadingVodMembership(false);
    }
  };

  const fetchVodData = async () => {
    setLoadingVods(true);
    try {
      const [availableRes, myVodsRes] = await Promise.all([
        axios.get(`${API}/livestream/available-vods`, getAuthHeaders()),
        axios.get(`${API}/livestream/my-vods`, getAuthHeaders())
      ]);
      setVodData(availableRes.data);
      setMyVods(myVodsRes.data.vods || []);
    } catch (err) {
      console.error('Error fetching VOD data:', err);
    } finally {
      setLoadingVods(false);
    }
  };

  const handlePurchaseVod = async (eventSlug) => {
    setPurchasingVod(eventSlug);
    const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
    
    try {
      const response = await axios.post(`${API}/livestream/payment/create-vod-checkout`, {
        event_slug: eventSlug,
        customer_id: customer.id,
        origin_url: window.location.origin
      }, getAuthHeaders());
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to initiate purchase' });
      setPurchasingVod(null);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/customer/dashboard`, getAuthHeaders());
      setDashboard(response.data);
      setFullName(response.data.customer?.full_name || '');
      setUsername(response.data.customer?.username || '');
      setInstagramUsername(response.data.customer?.instagram_username || '');
      
      // Sync localStorage with server data
      const customerData = {
        id: response.data.customer?.id,
        email: response.data.customer?.email,
        full_name: response.data.customer?.full_name,
        username: response.data.customer?.username,
        instagram_username: response.data.customer?.instagram_username,
        profile_picture: response.data.customer?.profile_picture
      };
      localStorage.setItem('livestreamCustomer', JSON.stringify(customerData));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('livestreamToken');
        localStorage.removeItem('livestreamCustomer');
        navigate('/live/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    setSavingProfile(true);
    setMessage({ type: '', text: '' });
    
    try {
      const updateData = {};
      if (username && !dashboard.customer.username) {
        updateData.username = username;
      }
      if (fullName !== dashboard.customer.full_name) {
        updateData.full_name = fullName;
      }
      if (instagramUsername !== (dashboard.customer.instagram_username || '')) {
        updateData.instagram_username = instagramUsername;
      }
      
      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'info', text: 'No changes to save' });
        setSavingProfile(false);
        return;
      }
      
      await axios.put(`${API}/customer/profile`, updateData, getAuthHeaders());
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      // Update local storage
      const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
      if (updateData.username) customer.username = updateData.username;
      if (updateData.full_name) customer.full_name = updateData.full_name;
      if (updateData.instagram_username !== undefined) customer.instagram_username = updateData.instagram_username;
      localStorage.setItem('livestreamCustomer', JSON.stringify(customer));
      
      fetchDashboard();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPicture(true);
    setMessage({ type: '', text: '' });
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/customer/profile/picture`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('livestreamToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessage({ type: 'success', text: 'Profile picture updated!' });
      fetchDashboard();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to upload picture' });
    } finally {
      setUploadingPicture(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    
    setChangingPassword(true);
    setMessage({ type: '', text: '' });
    
    try {
      await axios.put(`${API}/customer/password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, getAuthHeaders());
      
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    localStorage.removeItem('restrictedEvent');
    navigate('/live');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  const customer = dashboard?.customer;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Show event logo if restricted */}
            {/* Branding: VOD users see LTV VOD logo, event users see event branding */}
            {isVodUser ? (
              /* LTV VOD Branding for VOD users */
              <div 
                className="flex items-center gap-2 cursor-pointer" 
                onClick={() => navigate('/vod')}
              >
                <img 
                  src="/ltv-icon.png" 
                  alt="LTV Logo" 
                  className="w-7 h-7 sm:w-8 sm:h-8 object-contain brightness-0 invert"
                />
                <span className="font-bold text-sm sm:text-base text-white">LTV VOD</span>
              </div>
            ) : restrictedEvent?.custom_logo_url ? (
              <img 
                src={restrictedEvent.custom_logo_url} 
                alt={restrictedEvent.title || 'Event'} 
                className="h-8 sm:h-10 max-w-[120px] sm:max-w-[150px] object-contain"
              />
            ) : (
              /* Default: LTV Live Home button */
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/live')}
              >
                <Home className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            )}
            
            {/* Navigation buttons - always visible for all users */}
            {isVodUser ? (
              /* VOD-specific navigation */
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/vod')}
              >
                <Film className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Browse Videos</span>
              </Button>
            ) : (
              /* Live-specific navigation */
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/live/my-tickets')}
              >
                <Ticket className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">My Tickets</span>
              </Button>
            )}
          </div>
          <Button 
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Sign Out</span>
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-br from-white/5 to-white/0 rounded-xl sm:rounded-2xl border border-white/10">
          {/* Avatar */}
          <div className="relative group">
            {customer?.profile_picture ? (
              <img 
                src={customer.profile_picture} 
                alt="Profile"
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl object-cover"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <User className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
              </div>
            )}
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPicture}
              className="absolute inset-0 bg-[#0a0a0a]/50 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              {uploadingPicture ? (
                <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-white" />
              ) : (
                <Camera className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              )}
            </button>
            <input 
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePictureUpload}
              className="hidden"
            />
          </div>

          {/* Info */}
          <div className="flex-1 space-y-1 sm:space-y-2 text-center sm:text-left">
            <h1 className="text-xl sm:text-2xl font-bold">{customer?.full_name || 'User'}</h1>
            
            <div className="flex items-center justify-center sm:justify-start gap-2 text-gray-400 text-sm sm:text-base">
              <Mail className="h-4 w-4" />
              <span className="truncate max-w-[200px] sm:max-w-none">{customer?.email}</span>
            </div>
            
            {customer?.instagram_username && (
              <div className="flex items-center justify-center sm:justify-start gap-2 text-pink-400 text-sm sm:text-base">
                <Instagram className="h-4 w-4" />
                <span>@{customer.instagram_username}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 mt-2">
              {customer?.username ? (
                <Badge className="bg-purple-600">@{customer.username}</Badge>
              ) : (
                <Badge variant="outline" className="border-yellow-500/50 text-yellow-400">
                  No username set
                </Badge>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{dashboard?.total_purchases || 0}</div>
              <div className="text-sm text-gray-500">Purchases</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{dashboard?.upcoming_events?.length || 0}</div>
              <div className="text-sm text-gray-500">Upcoming</div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-500/20 border border-green-500/30 text-green-400' :
            message.type === 'error' ? 'bg-red-500/20 border border-red-500/30 text-red-400' :
            'bg-blue-500/20 border border-blue-500/30 text-blue-400'
          }`}>
            {message.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          <Button
            size="sm"
            className={activeTab === 'dashboard' 
              ? 'bg-gradient-to-r from-red-600 to-red-500 text-white border-0 rounded-xl' 
              : 'bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl'}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </Button>
          {/* Hide Recordings tab when in restricted mode */}
          {!restrictedEvent && (
            <Button
              size="sm"
              className={activeTab === 'recordings' 
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 rounded-xl' 
                : 'bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl'}
              onClick={() => setActiveTab('recordings')}
            >
              <Film className="h-4 w-4 mr-1" />
              Recordings
            </Button>
          )}
          {/* Hide VOD tab when in restricted mode */}
          {!restrictedEvent && (
            <Button
              size="sm"
              className={activeTab === 'vod' 
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 rounded-xl' 
                : 'bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl'}
              onClick={() => setActiveTab('vod')}
            >
              <Tv className="h-4 w-4 mr-1" />
              VOD
            </Button>
          )}
          <Button
            size="sm"
            className={activeTab === 'purchases' 
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0 rounded-xl' 
              : 'bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl'}
            onClick={() => setActiveTab('purchases')}
          >
            Purchases
          </Button>
          <Button
            size="sm"
            className={activeTab === 'settings' 
              ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white border-0 rounded-xl' 
              : 'bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl'}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Next Event */}
            {dashboard?.next_event && (
              <Card className="bg-gradient-to-br from-red-900/30 to-purple-900/30 border-red-500/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Play className="h-5 w-5 text-red-400" />
                    Your Next Event
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{dashboard.next_event.event_title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(dashboard.next_event.event_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatTime(dashboard.next_event.event_date)}
                        </span>
                      </div>
                    </div>
                    <Button 
                      className="bg-red-500 hover:bg-red-500"
                      onClick={() => navigate(`/live/watch/${dashboard.next_event.event_slug}`)}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {dashboard.next_event.event_status === 'live' ? 'Watch Now' : 'View Event'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Events */}
            {dashboard?.upcoming_events?.length > 0 && (
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Upcoming Streams</CardTitle>
                  <CardDescription>Events you have tickets for</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboard.upcoming_events.map((event) => (
                      <div 
                        key={event.ticket_id}
                        onClick={() => navigate(`/live/watch/${event.event_slug}`)}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {event.thumbnail_url ? (
                            <img src={event.thumbnail_url} alt="" className="w-16 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-16 h-12 rounded-lg bg-gradient-to-br from-red-900/50 to-purple-900/50 flex items-center justify-center">
                              <Play className="h-6 w-6 text-white/30" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-white">{event.event_title}</h4>
                            <p className="text-sm text-gray-400">
                              {formatDate(event.event_date)} at {formatTime(event.event_date)}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {!dashboard?.next_event && !dashboard?.upcoming_events?.length && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-12 text-center">
                  <Ticket className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-xl font-bold mb-2 text-white">No Upcoming Events</h3>
                  <p className="text-gray-400 mb-6">Browse events and purchase tickets to watch live streams</p>
                  <Button onClick={() => navigate('/live')} className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white border-0 rounded-xl">
                    Browse Events
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Recordings/VOD Tab */}
        {activeTab === 'recordings' && (
          <div className="space-y-6">
            {/* My Recordings */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Film className="h-5 w-5 text-purple-400" />
                  My Recordings
                </CardTitle>
                <CardDescription>Recordings you own from tickets or purchases</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingVods ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                  </div>
                ) : myVods.length > 0 ? (
                  <div className="grid gap-4">
                    {myVods.map((vod) => (
                      <div 
                        key={vod.id}
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/20 to-transparent rounded-xl border border-purple-500/20 hover:border-purple-500/40 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {vod.thumbnail_url ? (
                            <img src={vod.thumbnail_url} alt="" className="w-20 h-14 rounded-lg object-cover" />
                          ) : (
                            <div className="w-20 h-14 rounded-lg bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                              <Film className="h-6 w-6 text-white/30" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-white">{vod.title}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={vod.access_type === 'ticket' ? 'bg-green-600' : 'bg-purple-600'}>
                                {vod.access_type === 'ticket' ? 'Via Ticket' : 'Purchased'}
                              </Badge>
                              <span className="text-sm text-gray-400">
                                {formatDate(vod.event_date)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button 
                          className="bg-purple-600 hover:bg-purple-500"
                          onClick={() => navigate(`/live/watch/${vod.slug}`)}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Watch
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Film className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No recordings yet</p>
                    <p className="text-sm mt-1">Purchase event tickets or recordings to watch here</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available for Purchase */}
            {vodData.available_for_purchase.length > 0 && (
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5 text-green-400" />
                    Available Recordings
                  </CardTitle>
                  <CardDescription>Purchase recordings from past events</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {vodData.available_for_purchase.map((vod) => (
                      <div 
                        key={vod.id}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {vod.thumbnail_url ? (
                            <img src={vod.thumbnail_url} alt="" className="w-20 h-14 rounded-lg object-cover" />
                          ) : (
                            <div className="w-20 h-14 rounded-lg bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center">
                              <Film className="h-6 w-6 text-white/30" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-white">{vod.title}</h4>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-lg font-bold text-green-400">
                                £{vod.vod_price?.toFixed(2)}
                              </span>
                              <span className="text-sm text-gray-400">
                                Event: {formatDate(vod.event_date)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button 
                          className="bg-green-600 hover:bg-green-500"
                          onClick={() => handlePurchaseVod(vod.slug)}
                          disabled={purchasingVod === vod.slug}
                        >
                          {purchasingVod === vod.slug ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <ShoppingCart className="h-4 w-4 mr-2" />
                              Purchase
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* VOD Tab */}
        {activeTab === 'vod' && (
          <div className="space-y-6">
            {/* VOD Membership Status */}
            <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-400" />
                  VOD Membership
                </CardTitle>
                <CardDescription>Your Video on Demand subscription status</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingVodMembership ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                  </div>
                ) : vodMembership ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-white capitalize">{vodMembership.tier} Plan</span>
                          <Badge className="bg-green-600">Active</Badge>
                        </div>
                        <p className="text-gray-400 mt-1">
                          Renews on {new Date(vodMembership.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                        onClick={() => navigate('/vod/membership')}
                      >
                        Manage Plan
                      </Button>
                    </div>
                    
                    {vodMembership.tier === 'basic' && (
                      <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-500/30">
                        <p className="text-yellow-400 text-sm flex items-center gap-2">
                          <Crown className="h-4 w-4" />
                          Upgrade to Premium for access to all exclusive content
                        </p>
                        <Button
                          className="mt-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                          onClick={() => navigate('/vod/membership')}
                        >
                          Upgrade to Premium
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Tv className="h-12 w-12 mx-auto mb-3 text-purple-400 opacity-50" />
                    <h3 className="text-lg font-semibold text-white mb-2">No Active Membership</h3>
                    <p className="text-gray-400 mb-4">
                      Subscribe to get unlimited access to our VOD library
                    </p>
                    <Button
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                      onClick={() => navigate('/vod/membership')}
                    >
                      <Crown className="h-4 w-4 mr-2" />
                      View Membership Plans
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* VOD Purchases */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-green-400" />
                  VOD Purchases
                </CardTitle>
                <CardDescription>Videos you've purchased individually</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingVodMembership ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : vodPurchases.length > 0 ? (
                  <div className="grid gap-4">
                    {vodPurchases.map((video) => (
                      <div 
                        key={video.id}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {video.thumbnail_url ? (
                            <img src={video.thumbnail_url} alt="" className="w-20 h-14 rounded-lg object-cover" />
                          ) : (
                            <div className="w-20 h-14 rounded-lg bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                              <Film className="h-6 w-6 text-white/30" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-white">{video.title}</h4>
                            <p className="text-sm text-gray-400">{video.description?.slice(0, 60)}...</p>
                          </div>
                        </div>
                        <Button 
                          className="bg-purple-600 hover:bg-purple-500"
                          onClick={() => navigate(`/vod/watch/${video.id}`)}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Watch
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No purchased videos yet</p>
                    <Button
                      variant="link"
                      className="text-purple-400 mt-2"
                      onClick={() => navigate('/vod/browse')}
                    >
                      Browse VOD Library
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <div className="flex flex-wrap gap-4">
              <Button
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                onClick={() => navigate('/vod')}
              >
                <Tv className="h-4 w-4 mr-2" />
                Go to VOD Platform
              </Button>
              <Button
                variant="outline"
                className="border-white/20 text-white/70 hover:bg-white/10"
                onClick={() => navigate('/vod/my-library')}
              >
                <Film className="h-4 w-4 mr-2" />
                My VOD Library
              </Button>
            </div>
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Purchase History</CardTitle>
              <CardDescription>{dashboard?.total_purchases || 0} total purchases</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.purchases?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.purchases.map((purchase) => (
                    <div 
                      key={purchase.ticket_id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-white/5 rounded-xl gap-4"
                    >
                      <div>
                        <h4 className="font-medium text-white mb-1">{purchase.event_title}</h4>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                          <span>Event: {formatDate(purchase.event_date)}</span>
                          <span>Purchased: {formatDate(purchase.purchased_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-bold text-white">
                            £{purchase.amount_paid?.toFixed(2)}
                          </div>
                          <Badge className={
                            purchase.event_status === 'live' ? 'bg-red-500' :
                            purchase.event_status === 'upcoming' ? 'bg-blue-500' :
                            'bg-gray-500'
                          }>
                            {purchase.event_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No purchases yet
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Profile Settings */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Profile Settings</CardTitle>
                <CardDescription>Update your profile information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Username (for Live Chat)</Label>
                  {dashboard?.customer?.username ? (
                    <div className="mt-2">
                      <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-bold text-white">@{dashboard.customer.username}</p>
                          <p className="text-xs text-gray-400">Your username is set. You can use live chat!</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                        placeholder="Choose a username (letters & numbers only)"
                        className="bg-white/5 border-white/10 text-white mt-2"
                        maxLength={20}
                      />
                      <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-400 font-medium">Username required for live chat</p>
                          <p className="text-xs text-gray-400 mt-1">3-20 characters, letters and numbers only. Cannot be changed later!</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <div>
                  <Label className="text-gray-300">Full Name</Label>
                  <Input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your full name"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Instagram Username (optional)</Label>
                  <div className="relative mt-1">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-pink-400" />
                    <Input
                      value={instagramUsername}
                      onChange={(e) => setInstagramUsername(e.target.value.replace('@', ''))}
                      placeholder="yourusername"
                      className="bg-white/5 border-white/10 text-white pl-10"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Share your Instagram with other viewers</p>
                </div>
                
                <Button 
                  onClick={handleProfileUpdate}
                  disabled={savingProfile || (dashboard?.customer?.username && !fullName)}
                  className="bg-white text-black hover:bg-gray-200"
                >
                  {savingProfile ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Password Change */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Change Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Current Password</Label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      autoComplete="current-password"
                            required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">New Password</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      autoComplete="current-password"
                            required
                      minLength={6}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      autoComplete="current-password"
                            required
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={changingPassword}
                    className="bg-white text-black hover:bg-gray-200"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveCustomerAccount;
